export const AUTO_SOURCE_ID = "auto";

/**
 * 只保留目前支援的音源，並把舊版「全選」平順升級為新版自動模式。
 * 單一手動來源不會因新增線路而被改寫。
 */
export function normalizeSourceSelection(value, allowedIds, previousAutoIds = []) {
  const allowed = new Set(allowedIds);
  const selected = Array.isArray(value)
    ? [...new Set(value.filter((id) => allowed.has(id)))]
    : [];
  const wasPreviousAuto = previousAutoIds.length > 0
    && selected.length === previousAutoIds.length
    && previousAutoIds.every((id) => selected.includes(id));
  if (wasPreviousAuto) return [...allowedIds];
  return selected.length ? selected : [...allowedIds];
}

/** 單一音源視為手動模式；兩個以上來源仍由播放引擎自動備援。 */
export function sourceChoiceForSelection(selectedIds, allowedIds) {
  const selected = normalizeSourceSelection(selectedIds, allowedIds);
  return selected.length === 1 ? selected[0] : AUTO_SOURCE_ID;
}

export function selectionForSourceChoice(choice, allowedIds) {
  return allowedIds.includes(choice) ? [choice] : [...allowedIds];
}

/**
 * Provider 的技術健康狀態與「目前可否搜尋／解析／播放」分開判讀。
 * degraded 但仍具能力時顯示為可用，避免把區域限制誤報為完全離線。
 */
export function providerAvailability(provider) {
  if (!provider) return { label: "沒有資料", tone: "unknown", usable: false };
  if (provider.enabled === false) return { label: "已停用", tone: "unknown", usable: false };
  if (provider.configured === false) return { label: "未設定", tone: "unknown", usable: false };
  const circuitOpen = provider.circuitOpen === true
    || provider.operationCircuitOpen === true
    || provider.playbackCircuitOpen === true
    || provider.operationHealth?.circuitOpen === true
    || provider.playbackCircuit?.open === true;
  if (circuitOpen) return { label: "暫時熔斷", tone: "down", usable: false };
  const hasCapability = provider.searchAvailable === true
    || provider.resolveAvailable === true
    || provider.playbackAvailable === true;
  if (["unhealthy", "unavailable"].includes(provider.status)) {
    return { label: "離線", tone: "down", usable: false };
  }
  if (provider.status === "disabled") return { label: "已停用", tone: "unknown", usable: false };
  if (!hasCapability) return { label: "無法使用", tone: "down", usable: false };
  if (provider.status === "healthy") return { label: "正常", tone: "healthy", usable: true };
  if (provider.status === "degraded") return { label: "可用・受限", tone: "degraded", usable: true };
  return { label: "可用・待確認", tone: "unknown", usable: true };
}

export function providerFromHealth(healthData, providerId) {
  // /api/v1/providers 直接回傳 providers；保留對完整 /health 回應的相容性。
  const providers = Array.isArray(healthData?.providers)
    ? healthData.providers
    : healthData?.providers?.inspection?.providers;
  return Array.isArray(providers)
    ? providers.find((provider) => provider.id === providerId) || null
    : null;
}

export function aggregateApiHealth(healthData, providerIds) {
  if (!healthData) return { label: "檢查中", tone: "unknown" };
  const states = providerIds.map((id) => providerAvailability(providerFromHealth(healthData, id)));
  if (states.some((state) => state.tone === "healthy")) return { label: "服務正常", tone: "healthy" };
  if (states.some((state) => state.usable)) return { label: "服務可用", tone: "degraded" };
  return { label: "來源離線", tone: "down" };
}
